// Schnittstelle der Bestell-App – öffentliche API v1 (ENTSCHEIDUNGEN §3.4, erweitert durch
// docs/integration/bestell-app.md §3): Verträge für den technischen Zugang mit der Rolle „Vertriebskanal“ – Katalog,
// Verfügbarkeit und Preise, Kunden und Konten, Bestellung übergeben, Status, Storno, Zustellung melden – und die
// Ereignisse `sales.channel.*`, die ein Webhook an die App weiterreicht (nur als Auslöser). Preise werden IMMER hier
// ermittelt; die Preise der App dienen nur dem Vergleich und werden als Abweichung gemeldet, nie übernommen. Artikel
// und Gebinde nennt die App über Artikelnummer (SKU) und Einheitencode ('crate', 'bottle', 'keg' …); Mengen in
// Tausendstel des Gebindes (3 Kästen = 3000).
import { defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { zListPage } from '@shared/list'
import { z } from 'zod'
import { zDay, zEmptiesEntry, zTime } from './common'
import { zCheckoutIssue } from './checkout'
import { PROOF_BODY_BYTES, zDeliveryProofInput } from './afterDelivery'
import { brand } from '@shared/brand'

const zSku = z.string().trim().min(1, { error: 'Die Artikelnummer fehlt.' }).max(60)
const zUnitCode = z.string().trim().min(1).max(40)
const zText = (max: number) => z.string().trim().max(max)
const zExternalOrderId = zText(80).min(1, { error: 'Die Kennung der Bestellung fehlt.' })
const CUSTOMER_TYPES = ['private', 'business', 'public'] as const
const PRICE_MODES = ['gross', 'net'] as const

const zChannelLine = z.strictObject({
  sku: zSku,
  /** Einheitencode des Gebindes; ohne Angabe das Verkaufsgebinde des Artikels */
  unit: zUnitCode.optional(),
  /** Menge im Gebinde in Tausendstel (3 Kästen = 3000) */
  orderQty: z
    .int({ error: 'Bitte eine Menge angeben.' })
    .min(1, { error: 'Die Menge muss größer als 0 sein.' })
    .max(1_000_000_000, { error: 'Die Menge ist zu groß.' }),
  /** Einzelpreis, den die App gezeigt hat (Cent je Gebinde, in der Preisbasis des Kunden) – nur zum Vergleich */
  appUnitPriceCents: z.int().min(0).max(100_000_000).optional(),
  note: zText(200).optional(),
})

const zOneTimeCustomer = z.strictObject({
  name: zText(200).min(1, { error: 'Der Name fehlt.' }),
  street: zText(200).optional(),
  zip: zText(20).optional(),
  city: zText(120).optional(),
  phone: zText(60).optional(),
  email: z.email({ error: 'Die E-Mail-Adresse ist ungültig.' }).optional(),
})

const zChannelTotals = z.object({
  priceMode: z.enum(['gross', 'net']),
  netCents: z.int(),
  taxCents: z.int(),
  grossCents: z.int(),
  /** Pfand brutto, in grossCents enthalten */
  depositCents: z.int(),
})

const zChannelOrderLine = z.object({
  sku: z.string(),
  name: z.string(),
  unit: z.string(),
  orderQty: z.int(),
  /** hier ermittelter Einzelpreis je Gebinde in der Preisbasis des Auftrags */
  unitPriceCents: z.int(),
  /** Herkunft des Preises: „Preisliste Lieferpreis“, „Aktion …“ */
  priceLabel: z.string().nullable(),
  /** jetzt frei am liefernden Standort, im Gebinde (Tausendstel) */
  availableQty: z.int(),
  /** davon wäre Rückstand, wenn jetzt bestätigt würde */
  backorderQty: z.int(),
})

const zPriceDifference = z.object({
  sku: z.string(),
  unit: z.string(),
  appUnitPriceCents: z.int(),
  unitPriceCents: z.int(),
  differenceCents: z.int(),
})

export const channelSubmitOrder = defineContract({
  name: 'sales.channel.submitOrder',
  title: 'Bestellung eines Vertriebskanals übergeben',
  description: 'Nimmt eine Bestellung der Bestell-App als Auftrag im Zustand „Eingegangen“ an – mit hier ermittelten Preisen.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Legt aus einer Bestellung einen Auftrag im Zustand ' +
      '„Eingegangen“ an: Kunde über `customerNumber` oder als Einmalkunde (`oneTimeCustomer`, läuft auf den ' +
      'Sammelkunden `collectiveCustomerNumber`), Zeilen über Artikelnummer und Einheitencode, Menge in Tausendstel ' +
      'des Gebindes. Preise und Steuern ermittelt die Warenwirtschaft; weicht ein mitgeschickter Preis ab, ' +
      'steht das in `priceDifferences` – übernommen wird er nie. Dieselbe `externalOrderId` legt nichts Neues an, ' +
      'sondern liefert den vorhandenen Auftrag (`replayed: true`). Der Vertrieb erhält eine Aufgabe und bestätigt den ' +
      'Auftrag; erst dann wird reserviert. Für Mitarbeiter: sales.order.saveDraft.',
    keywords: ['Bestell-App', 'Online-Bestellung', 'Schnittstelle', 'Auftragseingang'],
  },
  input: z.strictObject({
    /** Kennung der Bestellung im Kanal – Schlüssel der Idempotenz */
    externalOrderId: zText(80).min(1, { error: 'Die Kennung der Bestellung fehlt.' }),
    /** Bestellnummer für Menschen: „AL-24851“ */
    externalNumber: zText(40).optional(),
    customerNumber: zText(40).optional(),
    oneTimeCustomer: zOneTimeCustomer.optional(),
    /** Sammelkunde für Einmalkunden (Kundennummer) – Pflicht zusammen mit oneTimeCustomer */
    collectiveCustomerNumber: zText(40).optional(),
    /** Code des liefernden Standorts ('ZL'); ohne Angabe der Standort des technischen Zugangs */
    locationCode: zText(20).optional(),
    deliveryDate: zDay.optional(),
    timeFrom: zTime.optional(),
    timeTo: zTime.optional(),
    /** vom Kunden angemeldetes Leergut – Hinweis für den Fahrer */
    announcedEmpties: z
      .array(z.strictObject({ label: zText(80).min(1), emptiesQty: z.int().min(1000).max(100_000_000), depositTypeId: zId.optional() }))
      .max(20)
      .optional(),
    note: zText(1000).optional(),
    /** Artikelzeilen; leer nur, wenn die Bestellung Leihartikel (`rentals`) enthält – Prüfung unten */
    lines: z.array(zChannelLine).max(200),
    // ---- additiv (Anbindung 15.10.2026): ohne diese Felder verhält sich der Vertrag wie zuvor ----
    /** pickup: Abholung am liefernden Standort – keine Fahrzeugplanung */
    fulfillment: z.enum(['delivery', 'pickup']).optional(),
    /** Lieferanschrift der Bestellung (auf Lieferschein und in der Fahreransicht) */
    shipTo: z
      .strictObject({
        name: zText(200).min(1, { error: 'Der Name der Lieferanschrift fehlt.' }),
        street: zText(200),
        zip: zText(20),
        city: zText(120),
        note: zText(500).optional(),
        phone: zText(60).optional(),
        /** Lage der Anschrift laut App (Adresssuche, Pin) – Merxo legt sie als Quelle „app“ ab (Disposition, §18.2) */
        lat: z.number().min(-90).max(90).optional(),
        lng: z.number().min(-180).max(180).optional(),
      })
      .refine((value) => (value.lat === undefined) === (value.lng === undefined), { error: 'Breiten- und Längengrad gehören zusammen.', path: ['lat'] })
      .optional(),
    /** Bestellzeichen des Kunden → Käuferreferenz des Auftrags */
    reference: zText(120).optional(),
    costCenter: zText(80).optional(),
    origin: z.enum(['app', 'phone', 'subscription']).optional(),
    paymentMethod: z.enum(['cash', 'ec', 'paypal', 'card', 'invoice', 'sepa']).optional(),
    commission: z.boolean().optional(),
    /** Gebühren und Gutschein der App – Beträge setzt die App, die Warenwirtschaft übernimmt sie (brutto, Cent) */
    charges: z
      .array(
        z.strictObject({
          code: z.enum(['delivery', 'carry', 'coupon']),
          label: zText(120).min(1, { error: 'Die Bezeichnung der Gebühr fehlt.' }),
          grossCents: z.int().min(0).max(10_000_000),
        }),
      )
      .max(5)
      .optional(),
    // ---- additiv (Lieferregeln, docs/integration/bestell-app.md §19.3.4): ohne diese Felder verhält sich der Vertrag wie zuvor ----
    /** Bestellzeitpunkt in der App (ISO) – der Bestellschluss gilt gegen ihn, wenn er nicht in der Zukunft und höchstens 48 h alt ist (C7) */
    placedAt: z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben.' }).optional(),
    /** der eingelöste Gutscheincode (bisher stand er nur im Label der Gebühr) */
    couponCode: zText(40).optional(),
    /** Die Kundschaft hat den Tragservice gewählt (Merxo prüft nur den Betrag) */
    carryService: z.boolean().optional(),
    // ---- additiv (Markt-Dashboard, docs/integration/bestell-app.md §20.3 S2) ----
    /** Abholcode der App (6 Zeichen) bei `fulfillment: 'pickup'`; ältere Apps ohne Code: Merxo vergibt einen und nennt ihn in der Antwort */
    pickupCode: z.string().trim().max(20).optional(),
    /** Leihartikel: Gebührenzeile zum Preis je Veranstaltung hier + Aufgabe „Leihartikel buchen“ für den Vertrieb */
    rentals: z
      .array(
        z.strictObject({
          sku: zSku,
          /** Stück */
          qty: z.int().min(1).max(999),
          /** (erster) Miettag */
          eventDate: zDay,
          appUnitPriceCents: z.int().min(0).optional(),
          // ---- additiv (Veranstaltungen, docs/integration/bestell-app.md §23.6) ----
          /** letzter Miettag (mehrtägig, beide Tage gebunden); ohne Angabe nur `eventDate` – wie bisher. Preis bleibt je Veranstaltung */
          toDate: zDay.optional(),
        }),
      )
      .max(50)
      .optional(),
  }).refine((input) => input.lines.length > 0 || (input.rentals?.length ?? 0) > 0, {
    // Bestellungen nur mit Leihartikeln (Zusammenspieltest 15.10.2026, Abweichung A12): mindestens eine Artikelzeile
    // ODER ein Leihartikel – ohne beides bleibt es beim bisherigen Satz am Feld `lines`
    error: 'Die Bestellung enthält keine Position.',
    path: ['lines'],
  }),
  output: z.object({
    orderId: zId,
    /** Auftragsnummer – erst nach der Bestätigung durch den Vertrieb */
    number: z.string().nullable(),
    status: z.string(),
    statusLabel: z.string(),
    /** true: Diese Bestellung lag schon vor, es wurde nichts angelegt */
    replayed: z.boolean(),
    totals: zChannelTotals,
    lines: z.array(zChannelOrderLine),
    priceDifferences: z.array(zPriceDifference),
    message: z.string(),
    /** Gebühren und Gutschein in der Reihenfolge der Eingabe – Betrag in der Basis des Auftrags (stets positiv; der Gutschein wird abgezogen) */
    charges: z.array(z.object({ code: z.string(), label: z.string(), amountCents: z.int() })),
    /** Leihartikel in der Reihenfolge der Eingabe – Preis je Veranstaltung und Stück in der Basis des Auftrags; `toDate` = letzter Miettag (additiv, nur bei mehrtägigen) */
    rentals: z.array(z.object({ sku: z.string(), qty: z.int(), eventDate: z.string(), unitPriceCents: z.int(), priceLabel: z.string().nullable(), toDate: z.string().optional() })),
    /** Prüfhinweise zu Fenster, Gebiet, Gebühren und Gutschein (§19.3.4); leer, wenn alles stimmt oder die Lieferregeln nicht eingerichtet sind. Bei Wiederholung die gespeicherten. */
    checkoutIssues: z.array(zCheckoutIssue),
    /** Abholcode des Auftrags (der gesendete, sonst der von Merxo vergebene); null bei Lieferung (§20.3 S2) */
    pickupCode: z.string().nullable(),
  }),
  permission: 'sales.order.write',
  risk: 'write',
  scope: 'record',
  publicApi: true,
  limits: { maxItems: 200, itemCount: (input) => input.lines.length + (input.rentals?.length ?? 0) },
})

/** Stand eines Auftrags der Bestell-App – Ausgabe von orderStatus und je Eintrag von orderStatuses. */
export const zChannelOrderStatus = z.object({
  orderId: zId,
  externalOrderId: z.string(),
  number: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string(),
  deliveryDate: z.string().nullable(),
  totals: zChannelTotals,
  lines: z.array(
    z.object({
      sku: z.string().nullable(),
      name: z.string().nullable(),
      unit: z.string().nullable(),
      orderQty: z.int(),
      deliveredQty: z.int(),
      unitPriceCents: z.int(),
    }),
  ),
  deliveries: z.array(
    z.object({
      id: zId,
      number: z.string().nullable(),
      status: z.string(),
      statusLabel: z.string(),
      tourDate: z.string().nullable(),
      deliveredAt: z.date().nullable(),
    }),
  ),
  /** Storno über sales.channel.cancelOrder ist noch möglich (nichts kommissioniert oder geliefert) */
  cancellable: z.boolean(),
  /** gemeldete Fehlversuche der Zustellung */
  failedAttempts: z.int(),
  invoices: z.array(z.object({ number: z.string().nullable(), status: z.string(), statusLabel: z.string() })),
  updatedAt: z.date(),
  /** Abholung in Merxo übergeben (§20.3 S2): Zeitpunkt und Name der Person; sonst null */
  pickedUpAt: z.date().nullable(),
  pickedUpBy: z.string().nullable(),
  // ---- additiv (Leihbuchungen am Auftrag, docs/integration/bestell-app.md §23.7) ----
  /**
   * Leihbuchungen im Verleih, die an diesem Auftrag hängen, ohne stornierte: Stück, Zeitraum (beide Tage gebunden) und Zustand
   * (`requested`, `confirmed`, `out`, `returned`). Diese Stücke zählt die Verfügbarkeit hier bereits – die App zieht sie nicht mehr selbst ab.
   */
  rentalBookings: z.array(z.object({ sku: z.string(), qty: z.int(), fromDate: z.string(), toDate: z.string(), status: z.string() })),
})

export const channelOrderStatus = defineContract({
  name: 'sales.channel.orderStatus',
  title: 'Status einer Kanal-Bestellung lesen',
  description: 'Zustand, Nummer, Summen und Lieferungen eines über einen Vertriebskanal eingegangenen Auftrags.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert zu einer `externalOrderId` den Auftrag: Zustand ' +
      '(Eingegangen, Bestätigt, Teilgeliefert, Geliefert, Abgebrochen), Auftragsnummer, Summen und die ' +
      'Lieferscheine mit ihrem Zustand (Lieferbereit, Unterwegs, Zugestellt).',
    keywords: ['Bestellstatus', 'Bestell-App', 'Schnittstelle'],
  },
  input: z.strictObject({ externalOrderId: zExternalOrderId }),
  output: zChannelOrderStatus,
  permission: 'sales.order.read',
  risk: 'read',
  scope: 'any',
  publicApi: true,
})

export const channelOrderStatuses = defineContract({
  name: 'sales.channel.orderStatuses',
  title: 'Status mehrerer Kanal-Bestellungen lesen',
  description: 'Zustand, Nummer, Summen, Lieferscheine und Rechnungen vieler über die Bestell-App eingegangener Aufträge auf einmal.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert zu bis zu 100 `externalOrderIds` je Auftrag dasselbe wie ' +
      'sales.channel.orderStatus. Unbekannte Kennungen und Aufträge an Standorten ohne Leserecht stehen in `unknownIds` – ' +
      'kein Fehler.',
    keywords: ['Bestellstatus', 'Bestell-App', 'Schnittstelle', 'Abgleich'],
  },
  input: z.strictObject({ externalOrderIds: z.array(zExternalOrderId).min(1, { error: 'Bitte mindestens eine Kennung nennen.' }).max(100) }),
  output: z.object({ orders: z.array(zChannelOrderStatus), unknownIds: z.array(z.string()) }),
  permission: 'sales.order.read',
  risk: 'read',
  scope: 'any',
  publicApi: true,
  limits: { maxItems: 100, itemCount: (input) => input.externalOrderIds.length },
})

export const channelCancelOrder = defineContract({
  name: 'sales.channel.cancelOrder',
  title: 'Bestellung eines Vertriebskanals stornieren',
  description: 'Bricht einen über die Bestell-App eingegangenen Auftrag ab – solange nichts kommissioniert oder geliefert ist.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Bricht den Auftrag zu `externalOrderId` ab: unbestätigt immer, ' +
      'bestätigt nur ohne gebuchten Lieferschein und ohne gelieferte Menge (Reservierungen werden frei, ein ' +
      'Lieferschein-Entwurf verworfen). Sonst 422 „bereits kommissioniert“. Schon abgebrochen → Erfolg mit `replayed`.',
    keywords: ['Storno', 'stornieren', 'Bestell-App', 'Schnittstelle', 'abbrechen'],
  },
  input: z.strictObject({ externalOrderId: zExternalOrderId, reason: zText(300).optional() }),
  output: z.object({ orderId: zId, status: z.string(), statusLabel: z.string(), replayed: z.boolean(), message: z.string() }),
  permission: 'sales.order.write',
  risk: 'write',
  scope: 'record',
  publicApi: true,
  effects: { reversible: false },
})

export const CHANNEL_FULFILLMENT_OUTCOMES = ['delivered', 'picked_up', 'failed'] as const

export const channelReportFulfillment = defineContract({
  name: 'sales.channel.reportFulfillment',
  title: 'Zustellung einer Kanal-Bestellung melden',
  description: 'Bucht Zustellung, Abholung oder Fehlversuch einer Bestellung der Bestell-App über den Lieferschein – mit Rückgabe und Kassieren an der Tür.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Meldet die Übergabe (`delivered`, `picked_up`) oder einen ' +
      'Fehlversuch (`failed`) zu `externalOrderId`. Fehlt der Lieferschein, kommissioniert die Warenwirtschaft alles ' +
      'Offene (FEFO) und bucht ihn; dann Tour starten und Zustellung bestätigen mit Alterskontrolle, mitgenommenem ' +
      'Rückgut (Art aus dem Katalog, Stück × 1000) und Kassieren an der Tür. Dieselbe `reportId` liefert das gespeicherte Ergebnis ' +
      '(`replayed: true`). Ein Fehlversuch storniert einen gebuchten Lieferschein bzw. legt eine Aufgabe zum Neueinplanen an.',
    keywords: ['Zustellung', 'Abholung', 'Fehlversuch', 'Bestell-App', 'Schnittstelle', 'Fahrer'],
  },
  input: z.strictObject({
    /** Bestellung der App … */
    externalOrderId: zExternalOrderId.optional(),
    /** … oder Auftrag aus Merxo (Disposition, §18.6.4): genau eines von beiden */
    orderId: zId.optional(),
    /** Tour und Stopp, auf denen die Übergabe geschah (Disposition) */
    tourId: zId.optional(),
    stopId: zId.optional(),
    /** Kennung der Meldung in der App – Idempotenz */
    reportId: zText(80).min(8, { error: 'Die Kennung der Meldung ist zu kurz.' }),
    outcome: z.enum(CHANNEL_FULFILLMENT_OUTCOMES),
    /** Zeitpunkt der Übergabe bzw. des Versuchs */
    at: z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben.' }),
    recipientName: zText(120).optional(),
    /** Pflicht true, wenn ein Artikel ein Mindestalter hat */
    ageChecked: z.boolean().optional(),
    /** Lieferung: Code des Fahrzeugs ('FZ-1'); Abholung: weglassen */
    vehicleCode: zText(20).optional(),
    driverName: zText(120).optional(),
    empties: z.array(zEmptiesEntry).max(50).optional(),
    payment: z.strictObject({ method: z.enum(['cash', 'card']), amountCents: z.int().min(0).max(10_000_000) }).optional(),
    emptiesPayoutCents: z.int().min(0).max(10_000_000).optional(),
    /** Pflicht bei failed */
    failureReason: zText(300).optional(),
    note: zText(500).optional(),
    /** tatsächliche Zahlart beim Kassieren (ersetzt den bisherigen Freitext in `note`) */
    paymentMethodUsed: z.enum(['cash', 'ec']).optional(),
    /** Zustellnachweis (bestell-app.md §22): Unterschrift ≤ 300 KB, Foto ≤ 2 MB, JPEG oder PNG als Base64 – nur bei Übergabe */
    proof: zDeliveryProofInput.optional(),
  }),
  output: z.object({
    orderId: zId,
    number: z.string().nullable(),
    status: z.string(),
    statusLabel: z.string(),
    replayed: z.boolean(),
    /** der Auftrag war schon vollständig geliefert – es wurde nichts gebucht */
    alreadyDelivered: z.boolean(),
    delivery: z.object({ id: zId, number: z.string().nullable(), status: z.string(), statusLabel: z.string() }).nullable(),
    dueCents: z.int(),
    collectedCents: z.int(),
    overpaidCents: z.int(),
    emptiesCreditCents: z.int(),
    message: z.string(),
  }),
  permission: 'sales.delivery.confirm',
  risk: 'financial',
  scope: 'record',
  publicApi: true,
  limits: { maxItems: 51, itemCount: (input) => (input.empties?.length ?? 0) + 1, bodyBytes: PROOF_BODY_BYTES },
})

// ---- Katalog, Kunden, Konten -----------------------------------------------------------------------------------

export const CHANNEL_MATERIALS = ['glas', 'pet', 'dose', 'fass', 'tetra', 'sonstiges'] as const

export const zChannelItem = z.object({
  itemId: zId,
  /** externe Kennung der Bestell-App – sonst aus der Artikelnummer abgeleitet */
  appId: z.string(),
  sku: z.string(),
  kind: z.enum(['goods', 'rental']),
  /** false: deaktiviert (gesperrt) oder ohne verkaufbares Gebinde */
  active: z.boolean(),
  /** Name ohne führende Marke und ohne Gebindetext am Ende: „Lagerbier Hell“ */
  title: z.string(),
  name: z.string(),
  brand: z.string().nullable(),
  description: z.string().nullable(),
  origin: z.string().nullable(),
  tags: z.array(z.string()),
  groupId: zId.nullable(),
  taxRateBp: z.int().nullable(),
  minAge: z.int(),
  alcoholBp: z.int().nullable(),
  material: z.enum(CHANNEL_MATERIALS),
  /** Füllmenge je Basiseinheit (Flasche) in ml */
  contentMl: z.int().nullable(),
  salesUnit: z
    .object({
      code: z.string(),
      label: z.string(),
      packagingText: z.string(),
      /** Tausendstel der Basiseinheit (Kasten 20 Fl. = 20000) */
      baseQty: z.int(),
      gtin: z.string().nullable(),
      depositTypeId: zId.nullable(),
    })
    .nullable(),
  image: z.object({ fileId: zId, sha256: z.string(), mime: z.string(), url: z.string() }).nullable(),
  /** jüngste Änderung an Artikel, Gebinden, Barcodes, Bildern, externer Kennung */
  updatedAt: z.date(),
})

export const zChannelDepositType = z.object({
  id: zId,
  key: z.string(),
  appKey: z.string().nullable(),
  name: z.string(),
  shortName: z.string().nullable(),
  /** je Stück bzw. Gebinde, brutto, heute (zusammengesetzt = Summe der Bestandteile) */
  amountCents: z.int(),
  composite: z.boolean(),
  returnable: z.boolean(),
  loose: z.boolean(),
  active: z.boolean(),
})

export const channelCatalog = defineContract({
  name: 'sales.channel.catalog',
  title: 'Katalog für einen Vertriebskanal lesen',
  description: 'Artikel mit Verkaufseinheit, Bild und Warengruppe – für den Spiegel der Bestell-App.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert die Artikel der Arten Ware und Mietartikel (auch ' +
      'gesperrte, dann `active: false`) mit Verkaufsgebinde, Barcode, Bild (`/api/files/<id>`), Warengruppe, ' +
      'Steuersatz, Mindestalter und Rückgutart – seitenweise. `updatedSince` liefert nur Geänderte; `serverTime` ist ' +
      'das nächste `updatedSince`. Warengruppen und Rückgutarten stehen vollständig auf der ersten Seite. Preise und ' +
      'Bestand: sales.channel.availability.',
    keywords: ['Katalog', 'Sortiment', 'Artikel', 'Bestell-App', 'Schnittstelle', 'Abgleich'],
  },
  input: z.strictObject({
    /** nur Artikel, die sich seit diesem Zeitpunkt geändert haben */
    updatedSince: z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben.' }).optional(),
    page: zListPage,
  }),
  output: z.object({
    /** Zeitpunkt für das nächste `updatedSince` (mit Sicherheitsabstand für noch offene Änderungen) */
    serverTime: z.date(),
    rows: z.array(zChannelItem),
    nextCursor: z.string().nullable(),
    groups: z.array(z.object({ id: zId, code: z.string().nullable(), name: z.string(), appKey: z.string().nullable(), sort: z.int() })),
    depositTypes: z.array(zChannelDepositType),
  }),
  permission: 'masterdata.item.read',
  risk: 'read',
  scope: 'any',
  publicApi: true,
})

export const channelCustomers = defineContract({
  name: 'sales.channel.customers',
  title: 'Kunden eines Vertriebskanals zuordnen',
  description: 'Kunden mit Kennung der Bestell-App und die genannten Kundennummern – für die Zuordnung in der App.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert alle Kunden mit externer Kennung der Bestell-App und ' +
      'zusätzlich die Kunden mit den genannten Nummern: Nummer, Kennung der App, Name, Art, aktiv/gesperrt und ' +
      'Preisbasis (brutto/netto).',
    keywords: ['Kunden', 'Zuordnung', 'Bestell-App', 'Schnittstelle'],
  },
  input: z.strictObject({ numbers: z.array(zText(40)).max(500).default([]) }),
  output: z.object({
    customers: z.array(
      z.object({
        partnerId: zId,
        number: z.string(),
        appId: z.string().nullable(),
        name: z.string(),
        type: z.enum(CUSTOMER_TYPES),
        active: z.boolean(),
        blocked: z.boolean(),
        priceMode: z.enum(PRICE_MODES),
        updatedAt: z.date(),
      }),
    ),
  }),
  permission: 'masterdata.customer.read',
  risk: 'read',
  publicApi: true,
  limits: { maxItems: 500, itemCount: (input) => input.numbers.length },
})

export const channelCustomerAccounts = defineContract({
  name: 'sales.channel.customerAccounts',
  title: 'Konditionen und Konten von Kunden eines Vertriebskanals lesen',
  description: 'Preisgruppe, Zahlungsbedingung, Kreditrahmen, offene Posten und Kontostände der genannten Kunden.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert ausschließlich für die genannten Kundennummern: ' +
      'Preisgruppe, Zahlungsbedingung, Kreditlimit mit beanspruchtem und freiem Rahmen (wie sales.credit.status), ' +
      'offene und überfällige Posten, Sperre mit Grund und das Rückgutkonto (je Art und in ganzen Einheiten der ' +
      'App). Unbekannte Nummern stehen in `unknownNumbers`.',
    keywords: ['Konditionen', 'Kreditlimit', 'Kundenkonto', 'Bestell-App', 'Schnittstelle'],
  },
  input: z.strictObject({ customerNumbers: z.array(zText(40)).min(1, { error: 'Bitte mindestens eine Kundennummer nennen.' }).max(50) }),
  output: z.object({
    accounts: z.array(
      z.object({
        partnerId: zId,
        number: z.string(),
        name: z.string(),
        type: z.enum(CUSTOMER_TYPES),
        active: z.boolean(),
        blocked: z.boolean(),
        blockReason: z.string().nullable(),
        priceGroup: z.object({ key: z.string(), name: z.string(), priceMode: z.enum(PRICE_MODES) }).nullable(),
        paymentTerm: z.object({ key: z.string(), name: z.string(), dueDays: z.int(), discountDays: z.int().nullable(), discountBp: z.int() }).nullable(),
        creditLimitCents: z.int().nullable(),
        creditUsedCents: z.int(),
        creditAvailableCents: z.int().nullable(),
        openItemsCents: z.int(),
        overdueCents: z.int(),
        deposits: z.object({
          totalCents: z.int(),
          balances: z.array(
            z.object({ depositTypeId: zId, key: z.string(), appKey: z.string().nullable(), name: z.string(), qty: z.int(), amountCents: z.int() }),
          ),
          /** ganze Gebinde je Pfandart der App */
          byAppKey: z.record(z.string(), z.int()),
        }),
        updatedAt: z.date(),
      }),
    ),
    unknownNumbers: z.array(z.string()),
  }),
  permission: 'sales.order.read',
  risk: 'read',
  scope: 'any',
  publicApi: true,
  limits: { maxItems: 50, itemCount: (input) => input.customerNumbers.length },
})

// ---- Ereignisse (Webhook „Bestell-App“ abonniert sales.channel.*) – nur Auslöser, die Daten holt die App per API ----

export const channelCatalogChanged = defineEvent({
  name: 'sales.channel.catalogChanged',
  title: 'Katalog eines Vertriebskanals geändert',
  payload: z.object({ reason: z.enum(['item', 'price', 'stock']), skus: z.array(z.string()).max(200) }),
})

export const channelOrderChanged = defineEvent({
  name: 'sales.channel.orderChanged',
  title: 'Bestellung eines Vertriebskanals geändert',
  // `externalOrderId` ist null bei einem Auftrag aus Merxo (Telefon, Theke; Markt 3a). Nur Kennung und Zustand, keine Kundendaten – auch keine
  // Kundennummer (das Ereignis geht für jeden Merxo-Auftrag hinaus): Die App fragt daraufhin `sales.channel.orderSnapshots` für ihre
  // verknüpften Kundinnen ab (Prüfung Markt MR5).
  payload: z.object({ externalOrderId: z.string().nullable(), orderId: zId, status: z.string() }),
})

export const channelCustomerChanged = defineEvent({
  name: 'sales.channel.customerChanged',
  title: 'Kunde eines Vertriebskanals geändert',
  payload: z.object({ customerNumber: z.string(), reason: z.enum(['master', 'deposit', 'invoice', 'credit']) }),
})

export const channelAvailability = defineContract({
  name: 'sales.channel.availability',
  title: 'Verfügbarkeit und Preise für einen Vertriebskanal lesen',
  description: 'Freier Bestand und gültiger Preis je Artikelnummer und Gebinde – für den Warenkorb der Bestell-App.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert für eine Liste von Artikelnummern je verkaufbarem ' +
      'Gebinde: frei verfügbare Menge am liefernden Standort (im Gebinde, Tausendstel – ohne Reserviertes und ' +
      'Gesperrtes), den für den Kunden und Tag gültigen Einzelpreis in dessen Preisbasis, Zuschläge je Gebinde und das ' +
      'Mindestalter. Unbekannte Artikelnummern stehen in `unknownSkus`.',
    keywords: ['Verfügbarkeit', 'Preise', 'Bestell-App', 'Schnittstelle', 'Warenkorb'],
  },
  input: z.strictObject({
    skus: z.array(zSku).min(1, { error: 'Bitte mindestens eine Artikelnummer nennen.' }).max(200),
    customerNumber: zText(40).optional(),
    locationCode: zText(20).optional(),
    date: zDay.optional(),
    /** true: auch Leihartikel beantworten (frei am Tag `date`, Preis je Veranstaltung) – sonst stehen sie in unknownSkus */
    rentals: z.boolean().default(false),
    /** additiv (Veranstaltungen, §23.6): Leihartikel über den Zeitraum `date` bis `toDate` – frei ist die kleinste Zahl der Tage */
    toDate: zDay.optional(),
  }),
  output: z.object({
    locationCode: z.string(),
    date: z.string(),
    priceMode: z.enum(['gross', 'net']),
    items: z.array(
      z.object({
        sku: z.string(),
        name: z.string(),
        minAge: z.int(),
        units: z.array(
          z.object({
            unit: z.string(),
            label: z.string(),
            /** Basismenge je Gebinde in Tausendstel */
            baseQty: z.int(),
            availableQty: z.int(),
            unitPriceCents: z.int().nullable(),
            priceLabel: z.string().nullable(),
            /** Pfand je Gebinde in Cent (brutto) */
            depositCents: z.int(),
            // ---- additiv (Anbindung 15.10.2026) ----
            priceSource: z.enum(['customer', 'promotion', 'group', 'base']).nullable(),
            /** Preis ohne Aktion/Sonderpreis; null, wenn gleich unitPriceCents */
            regularPriceCents: z.int().nullable(),
            /** nur bei Aktion: niedrigster Preis der 30 Tage vor Aktionsbeginn (PAngV § 11); null = Neuheit */
            referencePriceCents: z.int().nullable(),
            promotion: z.object({ name: z.string(), validFrom: z.string().nullable(), validTo: z.string().nullable() }).nullable(),
            /** Staffeln der wirksamen Liste ab mehr als einem Gebinde, aufsteigend */
            tiers: z.array(z.object({ minQty: z.int(), unitPriceCents: z.int() })),
          }),
        ),
      }),
    ),
    unknownSkus: z.array(z.string()),
  }),
  permission: 'sales.order.read',
  risk: 'read',
  scope: 'any',
  publicApi: true,
  limits: { maxItems: 200, itemCount: (input) => input.skus.length },
})

// ---- Werbe-Mitteilungen (docs/integration/bestell-app.md §17) ------------------------------------------------------
// Merxo führt die Ankündigungen (Modul Preise, pricing.campaign.*); die App holt bereite ab, übernimmt eine unmittelbar
// vor dem Versand (claimCampaign) und meldet das Ergebnis (reportCampaign). Recht: pricing.campaign.deliver.

const CAMPAIGN_DELIVER = 'pricing.campaign.deliver'
export const CHANNEL_CAMPAIGN_AUDIENCES = ['private', 'business', 'all'] as const
export const CHANNEL_CAMPAIGN_OUTCOMES = ['sent', 'failed'] as const

export const zChannelCampaign = z.object({
  campaignId: zId,
  /** ready = darf übernommen werden · fetched = übernommen, Ergebnis fehlt noch (nach einem Absturz fortsetzen) */
  state: z.enum(['ready', 'fetched']),
  /** Überschrift, höchstens 60 Zeichen */
  title: z.string(),
  /** Text, höchstens 178 Zeichen */
  body: z.string(),
  /** private = Privatkunden (Kundenart b2c) · business = freigeschaltete Geschäftskunden (b2b) · all = beide */
  audience: z.enum(CHANNEL_CAMPAIGN_AUDIENCES),
  /** Jugendschutz: höchste Altersgrenze der Artikel der Aktion (16 oder 18); null = keine. Mit Altersgrenze sendet die App nur an Personen mit Volljährigkeits-Bestätigung (§21.1) */
  minAge: z.union([z.literal(16), z.literal(18)]).nullable(),
  target: z.object({
    kind: z.enum(['offers', 'group', 'item']),
    /** Kennung in Merxo (Warengruppe bzw. Artikel); null bei den Angeboten */
    id: zId.nullable(),
    /** Warengruppe: Kategorie der App (appKey, sonst 'weitere') · Artikel: appId (§3.1) · Angebote: null */
    appKey: z.string().nullable(),
    /** nur bei Artikeln */
    sku: z.string().nullable(),
  }),
  /** Route der App, die der Tipp öffnet: '/angebote', '/sortiment/<Kategorie>', '/produkt/<appId>' */
  route: z.string(),
  promotion: z.object({ id: zId, name: z.string(), validFrom: z.string(), validTo: z.string() }),
  /** frühester Versand */
  sendAfter: z.date(),
  /** danach nicht mehr senden (Ende des letzten Aktionstags, Europe/Berlin) */
  expiresAt: z.date(),
  createdAt: z.date(),
  /** Zeitpunkt der Übernahme; null bei ready */
  claimedAt: z.date().nullable(),
})

export const channelCampaigns = defineContract({
  name: 'sales.channel.campaigns',
  title: 'Werbe-Mitteilungen für einen Vertriebskanal abrufen',
  description: 'Bereite und übernommene, noch nicht gemeldete Werbe-Mitteilungen – für den Versand im Vertriebskanal.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert die Werbe-Mitteilungen, die die App senden soll: bereit ' +
      '(`ready`, Zeitpunkt erreicht, Aktion freigegeben) und übernommen ohne gemeldetes Ergebnis (`fetched`) – älteste ' +
      'zuerst, höchstens 50. Abgebrochene und abgelaufene fehlen. Übernehmen: sales.channel.claimCampaign.',
    keywords: ['Push', 'Werbe-Mitteilung', 'Bestell-App', 'Schnittstelle', 'Abruf'],
  },
  input: z.strictObject({}),
  output: z.object({ serverTime: z.date(), campaigns: z.array(zChannelCampaign) }),
  permission: CAMPAIGN_DELIVER,
  risk: 'read',
  publicApi: true,
  assistant: 'never',
})

export const channelClaimCampaign = defineContract({
  name: 'sales.channel.claimCampaign',
  title: 'Werbe-Mitteilung für einen Vertriebskanal übernehmen',
  description: `Der Vertriebskanal übernimmt eine bereite Werbe-Mitteilung unmittelbar vor dem Versand – danach lässt sie sich in ${brand.name} nicht mehr abbrechen.`,
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Übernimmt eine bereite Werbe-Mitteilung zum Versand (ready → ' +
      'fetched) und liefert sie mit verbindlichem Inhalt. Schon übernommen → Erfolg mit `replayed`. Abgebrochen, ' +
      'abgelaufen, noch nicht fällig oder abgeschlossen → 422 mit `details.reason` (nicht senden).',
    keywords: ['Push', 'Werbe-Mitteilung', 'Bestell-App', 'Schnittstelle', 'übernehmen'],
  },
  input: z.strictObject({ campaignId: zId }),
  output: z.object({ campaign: zChannelCampaign, replayed: z.boolean() }),
  permission: CAMPAIGN_DELIVER,
  risk: 'write',
  publicApi: true,
  assistant: 'never',
})

const zCampaignCount = z.int({ error: 'Bitte eine ganze Zahl angeben.' }).min(0, { error: 'Die Zahl darf nicht negativ sein.' }).max(10_000_000)

export const channelReportCampaign = defineContract({
  name: 'sales.channel.reportCampaign',
  title: 'Ergebnis einer Werbe-Mitteilung eines Vertriebskanals melden',
  description: 'Der Vertriebskanal meldet, an wie viele Personen er eine übernommene Werbe-Mitteilung gesendet hat und wie viele er übersprungen hat.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Meldet das Ergebnis einer übernommenen Werbe-Mitteilung ' +
      '(fetched → sent | failed): gesendet an, ohne Einwilligung übersprungen, wegen Begrenzung übersprungen, Fehler, ' +
      'Zeitpunkt. Dieselbe Meldung noch einmal → `replayed`; eine andere → 409.',
    keywords: ['Push', 'Werbe-Mitteilung', 'Bestell-App', 'Schnittstelle', 'Ergebnis'],
  },
  input: z.strictObject({
    campaignId: zId,
    outcome: z.enum(CHANNEL_CAMPAIGN_OUTCOMES),
    counts: z.strictObject({
      /** Personen, an die gesendet wurde (mindestens ein Gerät hat angenommen) */
      sent: zCampaignCount,
      /** Personen der Zielgruppe mit Gerät, ohne Einwilligung „Angebote und Aktionen“ */
      skippedNoConsent: zCampaignCount,
      /** Personen mit Einwilligung, die heute bzw. in sieben Tagen schon genug Werbe-Mitteilungen hatten */
      skippedLimit: zCampaignCount,
      /** Personen, bei denen jede Zustellung scheiterte */
      failed: zCampaignCount,
      /** Personen übersprungen, weil die Bestätigung „mindestens 18 Jahre“ fehlt (nur bei Mitteilungen mit `minAge`; optional) */
      skippedAge: zCampaignCount.optional(),
    }),
    /** Zeitpunkt des Versands (Ende des Sendelaufs) */
    sentAt: z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben.' }),
    /** Pflicht bei failed: Grund in einem Satz */
    message: zText(300).optional(),
  }).refine((input) => input.outcome !== 'failed' || (input.message ?? '').length >= 3, { error: 'Bitte den Grund des Fehlschlags nennen.', path: ['message'] }),
  output: z.object({ campaignId: zId, state: z.enum(CHANNEL_CAMPAIGN_OUTCOMES), stateLabel: z.string(), replayed: z.boolean(), message: z.string() }),
  permission: CAMPAIGN_DELIVER,
  risk: 'write',
  publicApi: true,
  assistant: 'never',
})

export const channelCampaignRequested = defineEvent({
  name: 'sales.channel.campaignRequested',
  title: 'Werbe-Mitteilung für einen Vertriebskanal bereit oder abgebrochen',
  /** ready: abrufen und senden · cancelled: nicht (mehr) senden – abgebrochen oder abgelaufen */
  payload: z.object({ campaignId: zId, reason: z.enum(['ready', 'cancelled']) }),
})
